import { useEffect, useState, useCallback } from "react";
import { useAppDispatch } from "hooks";
import { FormLayout, TextField, Form, Button, Banner } from "@shopify/polaris";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { fetchProjectBoards } from "containers/project-boards/project-boards.slice";
import { setBoardName } from "containers/project-board/project-board-container.slice";
import {
  createProjectBoard,
  updateProjectBoard,
  fetchProjectBoardById,
} from "./project-board-form.service";
import {
  BOARD_COLOR_KEYS,
  gradientForKey,
} from "containers/project-boards/board-color";

function ProjectBoardForm({ onSubmit, id }) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { companyId } = useParams();
  const [name, setName] = useState();
  const [color, setColor] = useState(null);
  const [successMessage, setSuccessMessage] = useState();
  const [errMsg, setErrMsg] = useState();
  const [isLoading, setIsLoading] = useState(false);

  const handleFormSubmit = async () => {
    const formData = {
      id,
      name,
      color,
    };
    const action = id ? updateProjectBoard : createProjectBoard;
    try {
      setIsLoading(true);
      const result = await action(formData, companyId);
      dispatch(fetchProjectBoards(companyId));
      if (id) {
        dispatch(setBoardName(result?.name || name || ""));
      }
      setSuccessMessage("ok");
      if (onSubmit) {
        onSubmit(result);
      }
      setIsLoading(false);
    } catch (e) {
      console.error(e);
      setErrMsg("error");
      setIsLoading(false);
    }
  };

  const fetchData = useCallback(
    async (id) => {
      const data = await fetchProjectBoardById(companyId, id);
      setName(data.name);
      setColor(data.color || null);
    },
    [companyId]
  );

  useEffect(() => {
    if (id) {
      fetchData(id);
    }
  }, [id, fetchData]);

  return (
    <Form className="project-board-form">
      <FormLayout>
        <div className="form-warnings">
          {errMsg && (
            <div className="">
              <Banner status="critical">{t(errMsg)}</Banner>
            </div>
          )}
          {successMessage && (
            <div className="">
              <Banner status="info">{t(successMessage)}</Banner>
            </div>
          )}
        </div>
        <TextField
          label={t("project-board-name")}
          id="name"
          name="name"
          value={name}
          onChange={setName}
        />
        <div>
          <p
            style={{
              marginBottom: 6,
              fontSize: 13,
              color: "var(--p-color-text-secondary)",
            }}
          >
            {t("color", { defaultValue: "Color" })}
          </p>
          <div className="flex flex-wrap gap-2 max-md:gap-2.5">
            {BOARD_COLOR_KEYS.map((key) => (
              <button
                key={key}
                type="button"
                aria-label={key}
                title={key}
                onClick={() => setColor(key)}
                className="h-7 w-11 max-md:h-10"
                style={{
                  borderRadius: 6,
                  border: "none",
                  cursor: "pointer",
                  background: gradientForKey(key),
                  outline:
                    color === key
                      ? "2px solid var(--p-color-border-focus)"
                      : "none",
                  outlineOffset: 2,
                }}
              />
            ))}
          </div>
        </div>
        <Button
          primary
          onClick={handleFormSubmit}
          disabled={!name}
          loading={isLoading}
        >
          {t("save")}
        </Button>
      </FormLayout>
    </Form>
  );
}

export default ProjectBoardForm;
